'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
	getGetMeQueryKey,
	useUpdateMe,
} from '@cocoonspace/sdk-js/domains/profile/profile'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { useQueryClient } from '@tanstack/react-query'
import { LanguagesIcon } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { useLocale } from '~/hooks/use-locale.hook'
import {
	getLocalizedPath,
	isLocale,
	localeCookieName,
	stripLocaleFromPath,
} from '~/i18n/routing'
import { useAuth } from '~/providers/auth-provider'

export const LangSelector = ({ dense = false }: { dense?: boolean }) => {
	const queryClient = useQueryClient()
	const { isAuth } = useAuth()
	const router = useRouter()
	const pathname = usePathname() ?? ''
	const searchParams = useSearchParams()
	const locale = useLocale()
	const { mutateAsync: updateMe } = useUpdateMe()
	const currentPathRef = useRef(stripLocaleFromPath(pathname))
	const [selectedLocale, setSelectedLocale] = useState(locale)

	useEffect(() => {
		currentPathRef.current = stripLocaleFromPath(pathname)
	}, [pathname])

	useEffect(() => {
		setSelectedLocale(locale)
	}, [locale])

	const onUpdateLocale = useCallback(
		(value: string) => {
			if (!isLocale(value) || selectedLocale === value) return

			const search = searchParams.size ? `?${searchParams}` : ''

			const destination = getLocalizedPath(
				`${currentPathRef.current}${search}`,
				value,
			)

			setSelectedLocale(value)
			document.cookie = `${localeCookieName}=${value}; path=/; max-age=31536000; samesite=lax`
			router.push(destination)

			if (isAuth) {
				void updateMe({ data: { locale: value } })
					.then(() =>
						queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() }),
					)
					.catch(() => undefined)
			}
		},
		[searchParams, router, selectedLocale, updateMe, queryClient, isAuth],
	)

	return (
		<Select
			value={selectedLocale}
			onValueChange={onUpdateLocale}
		>
			<SelectTrigger className='hover:bg-muted/10 flex w-full items-center justify-start rounded-sm border-none px-2 md:h-8'>
				{!dense && <LanguagesIcon />}
				<SelectValue className='flex w-full flex-1 items-center justify-between' />
			</SelectTrigger>

			<SelectContent>
				<SelectItem value='fr'>
					<span className='mr-2'>🇫🇷</span>
					{!dense && <span className='text-sm'>Français</span>}
				</SelectItem>

				<SelectItem value='en'>
					<span className='mr-2'>🇬🇧</span>
					{!dense && <span className='text-sm'>English</span>}
				</SelectItem>
			</SelectContent>
		</Select>
	)
}
